import React, { useState, useEffect } from 'react';
import styled, { keyframes, createGlobalStyle, css } from 'styled-components';
import { useNavigate, useLocation } from 'react-router-dom';
import { ExternalLink, ChevronRight, Mail } from 'lucide-react';

// Global styles
const GlobalStyle = createGlobalStyle`
  * {
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, sans-serif;
  }
  
  html, body {
    margin: 0;
    padding: 0;
    overflow-x: hidden;
    min-height: 100vh;
    background: #0a0a0f;
  }
`;

// Animations
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(40px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0%, 100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(224, 5, 255, 0.4);
  }
  50% {
    transform: scale(1.05);
    box-shadow: 0 0 0 10px rgba(224, 5, 255, 0);
  }
`;

const float = keyframes`
  0%, 100% { 
    transform: translateY(0px) rotate(0deg); 
  }
  50% { 
    transform: translateY(-20px) rotate(5deg); 
  }
`;

const slideProgress = keyframes`
  from { width: 0; }
  to { width: 100%; }
`;

// Main container
const Container = styled.div`
  min-height: 100vh;
  background: #0a0a0f;
  background-image: 
    radial-gradient(circle at 20% 50%, rgba(120, 40, 200, 0.15) 0%, transparent 50%),
    radial-gradient(circle at 80% 80%, rgba(224, 5, 255, 0.15) 0%, transparent 50%),
    radial-gradient(circle at 40% 20%, rgba(167, 60, 255, 0.1) 0%, transparent 50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  position: relative;
  overflow: hidden;
`;

// Floating elements
const FloatingElement = styled.div`
  position: absolute;
  color: rgba(224, 5, 255, 0.2);
  font-size: 2rem;
  z-index: 0;
  ${props => css`
    animation: ${float} ${props.duration || '8s'} ease-in-out infinite;
    animation-delay: ${props.delay || '0s'};
  `}
`;

// Header
const Header = styled.header`
  text-align: center;
  margin-bottom: 2.5rem;
  z-index: 1;
  animation: ${fadeIn} 0.8s ease;
`;

const LogoWrapper = styled.div`
  width: 80px;
  height: 80px;
  margin: 0 auto 1.5rem;
  padding: 1rem;
  background: linear-gradient(135deg, rgba(224, 5, 255, 0.1), rgba(167, 60, 255, 0.1));
  border-radius: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  animation: ${pulse} 3s ease-in-out infinite;

  &::before {
    content: '';
    position: absolute;
    inset: -1px;
    border-radius: 24px;
    padding: 1px;
    background: linear-gradient(135deg, #E005FF, #6D28D9);
    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
  }

  svg {
    width: 48px;
    height: 48px;
  }
`;

const Title = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  background: linear-gradient(135deg, #FFFFFF 0%, #E005FF 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  letter-spacing: -0.02em;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1.1rem;
  color: rgba(255, 255, 255, 0.7);
  margin: 0;
  font-weight: 400;
  line-height: 1.5;
`;

// Progress bar
const ProgressContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 3rem;
  z-index: 1;
  animation: ${slideUp} 0.6s ease;
  animation-delay: 0.2s;
  animation-fill-mode: both;
`;

const ProgressStep = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 1.1rem;
  transition: all 0.3s ease;
  position: relative;
  z-index: 1;

  ${props => props.active && css`
    background: linear-gradient(135deg, #E005FF 0%, #A73CFF 100%);
    color: #ffffff;
    box-shadow: 0 8px 24px rgba(224, 5, 255, 0.4);
  `}

  ${props => props.completed && css`
    background: linear-gradient(135deg, #10B981 0%, #059669 100%);
    color: #ffffff;
  `}

  ${props => !props.active && !props.completed && css`
    background: rgba(255, 255, 255, 0.05);
    border: 2px solid rgba(255, 255, 255, 0.1);
    color: rgba(255, 255, 255, 0.5);
  `}
`;

const ProgressLine = styled.div`
  width: 80px;
  height: 2px;
  background: rgba(255, 255, 255, 0.1);
  margin: 0 1rem;
  position: relative;
  overflow: hidden;

  ${props => props.completed && css`
    &::after {
      content: '';
      position: absolute;
      left: 0;
      top: 0;
      height: 100%;
      width: 100%;
      background: linear-gradient(135deg, #E005FF 0%, #A73CFF 100%);
      animation: ${slideProgress} 0.5s ease;
    }
  `}

  @media (max-width: 768px) {
    width: 60px;
  }
`;

// Step card
const StepCard = styled.div`
  background: rgba(18, 18, 25, 0.8);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(224, 5, 255, 0.1);
  border-radius: 24px;
  padding: 2.5rem;
  max-width: 600px;
  width: 100%;
  box-shadow: 
    0 24px 48px rgba(0, 0, 0, 0.4),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);
  z-index: 1;
  animation: ${slideUp} 0.6s ease;
  animation-delay: 0.4s;
  animation-fill-mode: both;

  @media (max-width: 768px) {
    padding: 2rem;
    margin: 0 0.5rem;
  }
`;

const StepHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
`;

const StepIcon = styled.div`
  width: 56px;
  height: 56px;
  background: linear-gradient(135deg, rgba(224, 5, 255, 0.1), rgba(167, 60, 255, 0.1));
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
`;

const StepInfo = styled.div`
  flex: 1;
`;

const StepLabel = styled.div`
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.5);
  margin-bottom: 0.25rem;
  font-weight: 500;
`;

const StepTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: #ffffff;
  margin: 0;
`;

const StepContent = styled.div`
  text-align: center;
`;

const StepDescription = styled.p`
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.6;
  margin-bottom: 2rem;
`;

const ScreenshotPlaceholder = styled.div`
  background: rgba(10, 10, 15, 0.5);
  border: 1px solid rgba(224, 5, 255, 0.1);
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;

  img {
    max-width: 100%;
    max-height: 100%;
    border-radius: 8px;
  }
  
  ${props => props.step3 && css`
    min-height: 150px;
    padding: 1rem;
    img {
      max-width: 50%;
      max-height: 150px;
      object-fit: contain;
    }
  `}
`;

// Action button
const ActionButton = styled.button`
  background: linear-gradient(135deg, #E005FF 0%, #6D28D9 100%);
  border: none;
  border-radius: 12px;
  color: #FFFFFF;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 16px 32px;
  margin: 0.5rem;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, #6D28D9 0%, #E005FF 100%);
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover:not(:disabled)::before {
    opacity: 1;
  }

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 12px 24px rgba(224, 5, 255, 0.3);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  span, svg {
    position: relative;
    z-index: 1;
  }

  ${props => props.secondary && css`
    background: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(224, 5, 255, 0.3);

    &::before {
      background: rgba(224, 5, 255, 0.1);
    }
  `}

  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
  }
`;

// Button group
const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  justify-content: center;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
  }
`;

// Troubleshooting card
const TroubleshootingCard = styled.div`
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 12px;
  padding: 1rem;
  margin: 1rem 0;
  text-align: left;
`;

const TroubleshootingTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #FCA5A5;
  font-weight: 600;
  font-size: 0.9rem;
  margin-bottom: 0.5rem;
`;

const TroubleshootingText = styled.p`
  color: rgba(255, 255, 255, 0.7);
  margin: 0;
  font-size: 0.9rem;
  line-height: 1.4;
`;

// Support section
const SupportSection = styled.div`
  text-align: center;
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 1px solid rgba(224, 5, 255, 0.1);
  z-index: 1;
  animation: ${fadeIn} 0.6s ease;
  animation-delay: 0.4s;
  animation-fill-mode: both;
`;

const SupportText = styled.p`
  color: rgba(255, 255, 255, 0.6);
  margin: 0 0 0.5rem 0;
  font-size: 0.9rem;
`;

const SupportLink = styled.a`
  color: #E005FF;
  text-decoration: none;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s ease;

  &:hover {
    color: #A73CFF;
    transform: translateY(-1px);
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const PaymentRedirect = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState([]);

  // VSCode URI for payment success
  const vsCodeUri = `vscode://Sixth.sixth-ai?command=payment-success`;

  const handleOpenVSCode = () => {
    // Try to open VS Code URI
    window.location.href = vsCodeUri;
    
    // Mark step 1 as completed and move to step 2 after a delay
    // This gives time for the browser to attempt opening VS Code
    setTimeout(() => {
      if (!completedSteps.includes(1)) {
        setCompletedSteps(prev => [...prev, 1]);
        setCurrentStep(2);
      }
    }, 2000);
  };

  const handleContinueToDashboard = () => {
    // Clear VSCode-related localStorage items
    localStorage.removeItem('vscode_referrer');
    localStorage.removeItem('vscode_callback_url');

    // Redirect to dashboard using React Router
    navigate('/vscode/dashboard');
  };

  const markStepComplete = (stepNumber) => {
    if (!completedSteps.includes(stepNumber)) {
      setCompletedSteps(prev => [...prev, stepNumber]);
    }
    
    if (stepNumber < 3) {
      setCurrentStep(stepNumber + 1);
    }
  };

  // Removed auto-advance - user must click button to progress

  // Removed window focus handler to prevent auto-progression

  return (
    <>
      <GlobalStyle />
      <Container>
        {/* Floating background elements */}
        <FloatingElement style={{ top: '10%', left: '5%' }} duration="8s">
          💻
        </FloatingElement>
        <FloatingElement style={{ top: '20%', right: '10%' }} duration="10s" delay="1s">
          ✨
        </FloatingElement>
        <FloatingElement style={{ bottom: '30%', left: '10%' }} duration="7s" delay="2s">
          🔗
        </FloatingElement>
        
        {/* Header */}
        <Header>
          <LogoWrapper>
            <svg width="34" height="34" viewBox="0 0 34 34" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M30.2567 31.4665C24.8258 35.3273 18.0436 34.7252 13.3081 30.0044C11.8422 28.5431 11.0335 28.5268 9.49109 29.9277C7.16913 32.0367 4.05159 31.9635 1.78988 29.7469C-0.186653 27.8098 -0.558978 24.3323 0.971879 21.7637C1.2531 21.2918 1.27486 20.958 0.99758 20.4745C-0.324568 18.1689 -0.343021 15.8314 1.00522 13.5287C1.26356 13.0875 1.2763 12.7746 1.02935 12.3197C-0.683373 9.16444 -0.222355 6.13743 2.30384 3.73232C7.60106 -1.311 15.2719 -1.23559 20.6043 3.91225C22.2081 5.46051 22.8978 5.48136 24.5613 4.03185C26.8976 1.99621 29.9575 2.05858 32.1296 4.18611C34.1846 6.1989 34.5742 9.54463 33.0229 12.2246C32.7257 12.7381 32.736 13.0855 33.0294 13.5964C34.3153 15.8351 34.3206 18.1183 33.0623 20.3796C32.8193 20.8163 32.675 21.1466 32.96 21.6734C34.6951 24.8815 34.2241 27.8948 31.6402 30.3264C31.2194 30.7224 30.7546 31.0627 30.2567 31.4665ZM26.5092 28.802C27.635 28.2914 28.6121 27.5366 29.4876 26.6209C30.0339 26.0496 30.129 25.3268 29.9309 24.5798C29.7328 23.8331 29.1895 23.4418 28.5171 23.293C27.8061 23.1357 27.2887 23.5748 26.7946 24.044C25.2886 25.4741 23.5509 26.0244 21.5774 25.6873C20.1905 25.4503 19.0902 24.6671 18.0676 23.6478C14.1485 19.741 8.37365 19.719 4.75535 23.5578C3.89153 24.4743 3.78411 25.6063 4.48157 26.4431C5.18962 27.2925 6.16386 27.3707 7.05489 26.468C9.67788 23.8104 13.5595 24.2232 16.0181 26.7965C18.9831 29.8998 22.5528 30.4168 26.5092 28.802ZM9.10712 4.60121C7.49524 5.0705 6.04129 5.86103 4.79562 7.0907C4.41218 7.46922 4.11745 7.89843 4.04871 8.46134C3.94541 9.30714 4.23198 10.0021 4.91485 10.4036C5.65824 10.8407 6.38756 10.7046 7.03434 10.0987C8.15012 9.05337 9.38325 8.34718 10.9009 8.25638C12.9071 8.13636 14.5335 8.92139 15.9896 10.3888C19.7739 14.2027 25.3604 14.2707 29.1047 10.6039C30.0821 9.6466 30.2873 8.44175 29.6213 7.5694C28.8871 6.60772 27.841 6.63839 26.7665 7.65311C24.2441 10.035 20.7948 10.0151 18.3855 7.53275C15.7855 4.8539 12.7614 3.85009 9.10712 4.60121ZM26.7937 15.8513C23.9001 18.3488 20.7808 18.1996 18.0738 15.4498C16.2158 13.5624 14.0252 12.5742 11.4705 12.5527C8.85344 12.5306 6.58771 13.4893 4.68555 15.4312C3.84478 16.2896 3.79319 17.339 4.45215 18.1674C5.12474 19.0128 6.20408 19.1777 6.94843 18.3778C9.55535 15.5764 13.6213 16.0405 16.1042 18.6683C19.7453 22.5219 25.5854 22.4006 29.2038 18.7173C30.1239 17.7806 30.2884 16.5817 29.6104 15.7537C28.889 14.8726 27.875 14.874 26.7937 15.8513Z" fill="white"/>
            </svg>
          </LogoWrapper>
          <Title>Almost There!</Title>
          <Subtitle>
            Complete these quick steps to finalize your account upgrade
          </Subtitle>
        </Header>
        
        {/* Progress bar */}
        <ProgressContainer>
          <ProgressStep 
            completed={completedSteps.includes(1)} 
            active={currentStep === 1}
          >
            {completedSteps.includes(1) ? '✓' : '1'}
          </ProgressStep>
          <ProgressLine completed={completedSteps.includes(1)} />
          <ProgressStep 
            completed={completedSteps.includes(2)} 
            active={currentStep === 2}
          >
            {completedSteps.includes(2) ? '✓' : '2'}
          </ProgressStep>
          <ProgressLine completed={completedSteps.includes(2)} />
          <ProgressStep 
            completed={completedSteps.includes(3)} 
            active={currentStep === 3}
          >
            {completedSteps.includes(3) ? '✓' : '3'}
          </ProgressStep>
        </ProgressContainer>
        
        {/* Step content */}
        <StepCard>
          {/* Step 1 */}
          {currentStep === 1 && (
            <>
              <StepHeader>
                <StepIcon>🖱️</StepIcon>
                <StepInfo>
                  <StepLabel>Step 1 of 3</StepLabel>
                  <StepTitle>Open VS Code</StepTitle>
                </StepInfo>
              </StepHeader>
              
              <StepContent>
                <StepDescription>
                  Click the button below and you'll see a popup asking to open VS Code. 
                  Select "Open Visual Studio Code" to proceed.
                </StepDescription>
                
                <ScreenshotPlaceholder>
                  <img 
                    src="https://firebasestorage.googleapis.com/v0/b/sixth-v2.appspot.com/o/step1.png?alt=media&token=e45d7d87-1659-4b05-9e51-a43dc09ef1d5" 
                    alt="Browser popup showing 'Open Visual Studio Code' option" 
                  />
                </ScreenshotPlaceholder>
                
                <ActionButton onClick={handleOpenVSCode}>
                  <span>Open VS Code</span>
                  <ExternalLink size={20} />
                </ActionButton>
              </StepContent>
            </>
          )}
          
          {/* Step 2 */}
          {currentStep === 2 && (
            <>
              <StepHeader>
                <StepIcon>💻</StepIcon>
                <StepInfo>
                  <StepLabel>Step 2 of 3</StepLabel>
                  <StepTitle>Authorize in VS Code</StepTitle>
                </StepInfo>
              </StepHeader>
              
              <StepContent>
                <StepDescription>
                  Navigate to VS Code. You should see a modal asking to "Open a URI". 
                  Click the "Open" button to authorize Sixth AI.
                </StepDescription>
                
                <ScreenshotPlaceholder>
                  <img 
                    src="https://firebasestorage.googleapis.com/v0/b/sixth-v2.appspot.com/o/step2.png?alt=media&token=171c7221-ebd8-4a99-acde-140e7d12e9f8" 
                    alt="VS Code modal showing 'Open URI' dialog" 
                  />
                </ScreenshotPlaceholder>
                
                <TroubleshootingCard>
                  <TroubleshootingTitle>
                    ⚠️ Troubleshooting
                  </TroubleshootingTitle>
                  <TroubleshootingText>
                    Make sure the Sixth AI sidebar is currently open in VS Code. 
                    If you don't see the modal, click on the Sixth AI icon in the sidebar first.
                  </TroubleshootingText>
                </TroubleshootingCard>
                
                <ActionButton onClick={() => markStepComplete(2)}>
                  <span>I've clicked Open</span>
                  <ChevronRight size={20} />
                </ActionButton>
              </StepContent>
            </>
          )}
          
          {/* Step 3 */}
          {currentStep === 3 && (
            <>
              <StepHeader>
                <StepIcon>✅</StepIcon>
                <StepInfo>
                  <StepLabel>Step 3 of 3</StepLabel>
                  <StepTitle>Congrats on successfully upgrading your account!</StepTitle>
                </StepInfo>
              </StepHeader>
              
              <StepContent>
                <StepDescription>
                  After clicking the Open button, wait 3-5 seconds for the connection to establish. 
                  You're now ready to start using Sixth AI in VS Code!
                </StepDescription>
                
                <ScreenshotPlaceholder step3>
                  <img 
                    src="https://firebasestorage.googleapis.com/v0/b/sixth-v2.appspot.com/o/step3.png?alt=media&token=5340961e-bbc3-4311-a8d4-80fa7223d868" 
                    alt="Sixth AI successfully connected in VS Code" 
                  />
                </ScreenshotPlaceholder>
                
                <ButtonGroup>
                  <ActionButton onClick={handleContinueToDashboard}>
                    <span>Continue to Dashboard</span>
                    <ChevronRight size={20} />
                  </ActionButton>
                  <ActionButton secondary onClick={handleOpenVSCode}>
                    <span>Open VS Code Again</span>
                    <ExternalLink size={20} />
                  </ActionButton>
                </ButtonGroup>
              </StepContent>
            </>
          )}
        </StepCard>
        
        {/* Support section */}
        <SupportSection>
          <SupportText>Having trouble with this process?</SupportText>
          <SupportLink href="mailto:ope@trysixth.com">
            <Mail size={18} />
            Contact support at ope@trysixth.com
          </SupportLink>
        </SupportSection>
      </Container>
    </>
  );
};

export default PaymentRedirect;